<script setup lang="ts">
// What actually became of the emails we sent to a participant or a trainer (RDT-360, RDT-365).
//
// Our own record used to say only that Scaleway's relay accepted a message, which is why support had
// to open the Scaleway console to answer "the questionnaire never arrived". This asks Scaleway the
// same question from here.
//
// Scaleway knows nothing about our domain — it can only be asked about an address, and an address is
// not a person: two participants share one when a trainer tests with their own, the same person
// appears in several projects, and the address also receives mail in its trainer role. The
// e-mail-service therefore records the id Scaleway assigns at send time against a participant or
// trainer reference, and tells us here which emails are this one's. That replaced an earlier guess
// based on how close an email's timestamp was to a "mail sent" activity, which credited a
// participant with an email sent 16 months after their last activity.
//
// Anything unattributed is kept, behind its own heading: an email sent before the recording existed,
// or one whose reply carried no id, is not known to be this one's - but it is still worth seeing.
//
// The three failure shapes are kept apart on purpose — an environment without a Scaleway key, a
// lookup that broke, and an address that genuinely received nothing all look identical otherwise,
// and the wrong reading sends support down the wrong path.
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { EmailServiceAPI } from "../api";
import {
    DeliveredEmail,
    DeliveryAttempt,
    DeliveryBlock,
    DeliveryFlag,
    DeliveryState,
    EmailReferenceType,
} from "../types/PersologOnlineTypes.ts";
import { formatDateTime } from "../utils/dateFormat.ts";

const props = defineProps({
    /** The address to ask Scaleway about. */
    email: {
        type: String,
        required: true,
    },
    /** Whose emails these are, which is what the e-mail-service matches its own records against. */
    referenceType: {
        type: String as () => EmailReferenceType,
        required: true,
    },
    referenceId: {
        type: String,
        required: true,
    },
    /**
     * How far back to ask about the *address*. Leave unset unless there is a reason not to: Scaleway
     * cannot serve a wide window for this query shape. Filtering by recipient *and* sender is fast
     * up to about 180 days (0.2 s) and then falls off a cliff — 365 days answers `503 upstream
     * request timeout` after 30 seconds, which surfaced here as "could not load".
     *
     * This no longer decides how far back the participant's own emails are visible: those are
     * fetched by the id recorded for them, whatever their age (RDT-384). The window only decides
     * how much surrounding traffic to the same address comes along as context.
     */
    days: {
        type: Number,
        default: undefined,
    },
});

const { t, te } = useI18n();

const emails = ref<DeliveredEmail[]>([]);
const blocks = ref<DeliveryBlock[]>([]);
const loading = ref(false);
/** "unavailable" = no Scaleway key here (503); "failed" = the lookup itself broke. */
const problem = ref<"unavailable" | "failed" | null>(null);
/**
 * True when the service could not fetch every email it has recorded for this participant or
 * trainer. Said out loud rather than passed over: a support view silently one row short is how the
 * attribution bug went unnoticed, and the missing row is exactly the one being asked about.
 */
const incomplete = ref(false);

/** Flags that explain a failure to a human; the rest are noise in a support view. */
const NOTEWORTHY_FLAGS: DeliveryFlag[] = [
    DeliveryFlag.HARD_BOUNCE,
    DeliveryFlag.MAILBOX_NOT_FOUND,
    DeliveryFlag.MAILBOX_FULL,
    DeliveryFlag.SPAM,
    DeliveryFlag.BLOCKLISTED,
    DeliveryFlag.SOFT_BOUNCE,
    DeliveryFlag.GREYLISTED,
];

// Icon and colour per state, mirroring the participant status tag so the two read as one system.
const PRESENTATION: Record<string, { icon: string; severity: string }> = {
    [DeliveryState.SENT]: { icon: "pi pi-check", severity: "success" },
    [DeliveryState.FAILED]: { icon: "pi pi-times", severity: "danger" },
    [DeliveryState.SENDING]: { icon: "pi pi-send", severity: "info" },
    [DeliveryState.NEW]: { icon: "pi pi-clock", severity: "secondary" },
    [DeliveryState.CANCELED]: { icon: "pi pi-ban", severity: "warn" },
    [DeliveryState.UNKNOWN]: { icon: "pi pi-question", severity: "secondary" },
};

const presentationOf = (status: DeliveryState) =>
    PRESENTATION[status] ?? { icon: "pi pi-question", severity: "secondary" };

/** A soft bounce or greylisting resolves itself; the rest is something support has to act on. */
const flagSeverity = (flag: DeliveryFlag) =>
    flag === DeliveryFlag.SOFT_BOUNCE || flag === DeliveryFlag.GREYLISTED ? "warn" : "danger";

/** Falls back to the raw value, so a status or flag Scaleway adds later is still readable. */
const label = (group: string, value: string) => {
    const key = `participants.delivery.${group}.${value.toLowerCase()}`;
    return te(key) ? t(key) : value;
};

const statusLabel = (status: DeliveryState) => label("status", status);

const flagLabel = (flag: string) => label("flag", flag);

const noteworthyFlags = (mail: DeliveredEmail) => mail.flags.filter((flag) => NOTEWORTHY_FLAGS.includes(flag));

/**
 * Scaleway's own wording, but only when it adds something: a delivered email is annotated "success",
 * which just repeats the tag next to it.
 */
const detailsOf = (mail: DeliveredEmail) => (mail.status === DeliveryState.SENT ? null : mail.statusDetails);

/**
 * The attempts that did not succeed carry the recipient server's own words, which is what support
 * quotes back to the trainer.
 *
 * Anything outside 2xx/3xx counts: Scaleway reports `0` when an attempt never reached an SMTP server
 * and `-1` for a drop it handled itself — and that second case is where the blocked and
 * mailbox-not-found bounces live, so filtering on 4xx/5xx alone would hide exactly the lines that
 * explain why nothing arrived.
 */
const unsuccessfulAttempts = (mail: DeliveredEmail) =>
    mail.attempts.filter((attempt) => (attempt.code < 200 || attempt.code >= 400) && attempt.message);

/** "550 5.1.1 unknown" for a real SMTP code, the bare message for Scaleway's own 0 and -1 markers. */
const attemptLine = (attempt: DeliveryAttempt) =>
    attempt.code > 0 ? `${attempt.code} ${attempt.message}` : attempt.message;

const attributed = computed(() => emails.value.filter((mail) => mail.attributed));
const other = computed(() => emails.value.filter((mail) => !mail.attributed));
const hasBlocks = computed(() => blocks.value.length > 0);

const load = async () => {
    loading.value = true;
    problem.value = null;
    incomplete.value = false;
    try {
        const report = await EmailServiceAPI.getDeliveryStatus(props.email, {
            days: props.days,
            referenceType: props.referenceType,
            referenceId: props.referenceId,
        });
        emails.value = report.emails;
        blocks.value = report.blocks;
        incomplete.value = report.complete === false;
    } catch (error) {
        console.error("Could not load the delivery status of the participant's emails", error);
        emails.value = [];
        blocks.value = [];
        incomplete.value = false;
        const status = (error as { response?: { status?: number } })?.response?.status;
        problem.value = status === 503 ? "unavailable" : "failed";
    } finally {
        loading.value = false;
    }
};

watch(() => [props.email, props.referenceType, props.referenceId, props.days], load, { immediate: true });
</script>

<template>
    <div :data-cy="`email-delivery-${referenceType}-${referenceId}`">
        <h4>{{ t("participants.delivery.title") }}</h4>

        <div v-if="loading" class="flex items-center gap-2">
            <i class="pi pi-spin pi-spinner"></i>
        </div>

        <!-- Not an error: this environment simply cannot look it up. -->
        <Message v-else-if="problem === 'unavailable'" severity="secondary" :closable="false">
            {{ t("participants.delivery.unavailable") }}
        </Message>
        <Message v-else-if="problem === 'failed'" severity="error" :closable="false">
            {{ t("participants.delivery.error") }}
        </Message>

        <template v-else>
            <!-- Leads the panel: while the address is blocked, nothing sent to it arrives at all. -->
            <Message v-if="hasBlocks" severity="error" :closable="false" class="mb-2">
                <div v-for="block in blocks" :key="`${block.type}-${block.endsAt}`">
                    {{ t("participants.delivery.blocked", { type: flagLabel(block.type) }) }}
                    <span v-if="block.reason"> — {{ block.reason }}</span>
                    <span v-if="block.endsAt">
                        ({{ t("participants.delivery.blockedUntil", { date: formatDateTime(block.endsAt) }) }})
                    </span>
                </div>
            </Message>

            <!-- Not a failure of the whole lookup: what came back is shown, and so is the gap. -->
            <Message v-if="incomplete" severity="warn" :closable="false" class="mb-2" data-cy="delivery-incomplete">
                {{ t("participants.delivery.incomplete") }}
            </Message>

            <span v-if="!attributed.length" data-cy="delivery-none-attributed">
                {{ t("participants.delivery.empty") }}
            </span>

            <div
                v-for="mail in attributed"
                :key="mail.id"
                class="mb-3"
                :data-cy="`delivery-email-${mail.id}`"
            >
                <div class="flex items-center gap-2 flex-wrap">
                    <Tag
                        :icon="presentationOf(mail.status).icon"
                        :severity="presentationOf(mail.status).severity"
                        :value="statusLabel(mail.status)"
                        style="font-weight: normal"
                    />
                    <Tag
                        v-for="flag in noteworthyFlags(mail)"
                        :key="flag"
                        :severity="flagSeverity(flag)"
                        :value="flagLabel(flag)"
                        style="font-weight: normal"
                    />
                    <span>{{ mail.subject }}</span>
                    <small v-if="mail.sentAt" class="p-text-secondary">{{ formatDateTime(mail.sentAt) }}</small>
                    <small v-if="mail.tryCount > 1" class="p-text-secondary">
                        {{ t("participants.delivery.tries", { count: mail.tryCount }) }}
                    </small>
                </div>
                <!-- Verbatim, because the recipient server's wording is what support passes on. -->
                <small v-if="detailsOf(mail)" class="p-text-secondary block">{{ detailsOf(mail) }}</small>
                <small
                    v-for="attempt in unsuccessfulAttempts(mail)"
                    :key="attempt.rank"
                    class="p-text-secondary block"
                >
                    {{ attemptLine(attempt) }}
                </small>
            </div>

            <!--
                Kept rather than dropped: the attribution relies on the participant's activity log, and
                an email that is missing from it is still worth seeing - just not as this participant's.
            -->
            <Panel
                v-if="other.length"
                :header="t('participants.delivery.otherHeader', { count: other.length, email })"
                toggleable
                collapsed
                class="mt-2"
                data-cy="delivery-other-emails"
            >
                <small class="p-text-secondary block mb-2">{{ t("participants.delivery.otherHint") }}</small>
                <div v-for="mail in other" :key="mail.id" class="mb-2" :data-cy="`delivery-other-${mail.id}`">
                    <div class="flex items-center gap-2 flex-wrap">
                        <Tag
                            :icon="presentationOf(mail.status).icon"
                            :severity="presentationOf(mail.status).severity"
                            :value="statusLabel(mail.status)"
                            style="font-weight: normal"
                        />
                        <span>{{ mail.subject }}</span>
                        <small v-if="mail.sentAt" class="p-text-secondary">{{ formatDateTime(mail.sentAt) }}</small>
                    </div>
                </div>
            </Panel>
        </template>
    </div>
</template>
